import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isEmpty } from 'lodash'
import { IconArrowLeft, IconPlus, IconPencil, IconBookmark } from '@tabler/icons-react'
import pluralize from 'pluralize'

import { closeModal, openModal } from '@/reducers/ui/ui.redux'
import { deleteSavedFilter } from '@/reducers/savedFilters/savedFilters.actions'
import { BasicModal } from '@/components/layout/modals/BasicModal'
import { getFilterAgents } from '@/reducers/savedFilters/savedFilters.helpers'
import { CHANNEL_TYPE } from '@/utils/constants'
import { Button } from '@/components/buttons/Button'

import { SavedFilterForm } from './SavedFilterForm'
import { SavedFiltersList } from './SavedFiltersList'
import { AgentList } from './AgentList'
import './SavedFilters.scss'
import { SearchableDropdown } from '../dropdowns/SearchableDropdown'

const MANAGE_FILTERS_MODAL = 'savedFilterModal/manageFilters'
const CREATE_EDIT_FILTER_MODAL = 'savedFilterModal/createFilter'

export const SavedFilters = ({
  filterType,
  filterFormOptions = [],
  showAgentList = false,
  modalProps = {},
  noFiltersTitle,
  noFiltersMessage,
  noFiltersButtonLabel,
  selectedSavedFilterId,
  handleSelectSavedFilter,
  currentChannel = CHANNEL_TYPE.CALL,
  orgActiveChannels,
}) => {
  const dispatch = useDispatch()
  const { currentlyOpenModalId: modal } = useSelector((state) => state.ui)
  const { savedFilterList, loading: savedFiltersLoading } = useSelector(
    (state) => state.savedFilters
  )
  const [currentlyEditingFilter, setCurrentlyEditingFilter] = useState(null)

  const allSavedFilters = savedFilterList[filterType] || []

  const savedFilterOptions = allSavedFilters.map((filter) => ({
    value: filter.uuid,
    label: filter.name,
    is_default: filter.is_default,
    channel: filter.filters?.channel || CHANNEL_TYPE.CALL,
  }))

  const currentlySelectedFilter = selectedSavedFilterId
    ? allSavedFilters.find((filter) => filter.uuid === selectedSavedFilterId)
    : null
  const filterAgents = getFilterAgents(currentlySelectedFilter)

  const showNoFiltersMessage = isEmpty(allSavedFilters)

  const openManageFiltersModal = () => {
    dispatch(openModal(MANAGE_FILTERS_MODAL))
  }

  const openCreateFilterModal = () => {
    setCurrentlyEditingFilter(null)
    dispatch(openModal(CREATE_EDIT_FILTER_MODAL))
  }

  const actions = [
    {
      key: 'create-filters',
      fn: openCreateFilterModal,
      label: (
        <div className="flex-align-center small-gap">
          <IconPlus className="icon-svg status-brand" />
          Create New Filter
        </div>
      ),
    },
    {
      key: 'manage-filters',
      fn: openManageFiltersModal,
      label: (
        <div className="flex-align-center small-gap">
          <IconPencil className="icon-svg status-brand" />
          Manage My Filters
        </div>
      ),
    },
  ]

  const handleCloseModal = () => {
    dispatch(closeModal())
  }

  const handleBackButtonClick = () => {
    dispatch(openModal(MANAGE_FILTERS_MODAL))
  }

  const handleEditFilter = (filterId) => {
    const updatedFilter = allSavedFilters.find((filter) => filter.uuid === filterId)

    setCurrentlyEditingFilter(updatedFilter)
    dispatch(openModal(CREATE_EDIT_FILTER_MODAL))
  }

  const handleDeleteFilter = (filterId) => {
    dispatch(deleteSavedFilter(filterId, filterType))
  }

  return (
    <>
      <div data-testid="manage-filters" style={{ display: 'flex', gap: '0.75rem' }}>
        {showAgentList && filterAgents && <AgentList agents={filterAgents} />}
        <SearchableDropdown
          dataTestId="saved-filters"
          buttonLabel="Saved Filters"
          additionalIcon={<IconBookmark />}
          additionalLabel={selectedSavedFilterId ? 'Filter: ' : null}
          inputProps={{ placeholder: 'Search Filters...' }}
          options={savedFilterOptions}
          loading={savedFiltersLoading[filterType]}
          placement="bottom-end"
          handleSelect={handleSelectSavedFilter}
          actions={actions}
          selected={selectedSavedFilterId}
          pinnedAccessor="is_default"
          showResultsHeader
          isClearable
        />
      </div>

      {modal === MANAGE_FILTERS_MODAL && (
        <BasicModal
          data-testid="manage-filters-modal"
          className="saved-filters-modal"
          title="My Saved Filters"
          onClose={handleCloseModal}
          show={!!modal}
          size="small"
          showCloseButton={false}
          canCloseOnDimmerClick={false}
          footer={
            <Button brand onClick={handleCloseModal}>
              Done
            </Button>
          }
          {...modalProps}
        >
          {showNoFiltersMessage ? (
            <div className="no-filters-wrapper">
              <h1>{noFiltersTitle || 'You have no filters'}</h1>
              <div className="text-center">
                {noFiltersMessage || 'Get started by creating a new filter'}
              </div>
              <Button brand data-testid="create-filter-button" onClick={openCreateFilterModal}>
                {noFiltersButtonLabel || 'Create Filter'}
              </Button>
            </div>
          ) : (
            <>
              {!isEmpty(allSavedFilters) && (
                <div className="saved-filters-summary">
                  <span className="saved-filters-count">
                    {pluralize('saved filter', allSavedFilters.length, true)}
                  </span>
                  <Button data-testid="new-filter-button" onClick={openCreateFilterModal}>
                    Create Filter
                  </Button>
                </div>
              )}

              <SavedFiltersList
                savedFilters={allSavedFilters}
                onEditClick={handleEditFilter}
                onDeleteClick={handleDeleteFilter}
              />
            </>
          )}
        </BasicModal>
      )}

      {modal === CREATE_EDIT_FILTER_MODAL && (
        <BasicModal
          data-testid="new-filter-modal"
          title={
            <div className="flex-align-center medium-gap">
              <Button square onClick={handleBackButtonClick}>
                <IconArrowLeft />
              </Button>
              <div>{currentlyEditingFilter ? 'Edit' : 'New'} Filter</div>
            </div>
          }
          onClose={handleCloseModal}
          canCloseOnDimmerClick={false}
          show={!!modal}
          size="small"
          showCloseButton={false}
        >
          <SavedFilterForm
            currentlyEditingFilter={currentlyEditingFilter}
            filterFormOptions={filterFormOptions}
            filterType={filterType}
            currentChannel={currentChannel}
            orgActiveChannels={orgActiveChannels}
          />
        </BasicModal>
      )}
    </>
  )
}
